export const UAE_PASS_COPY = {
  signInButton: 'Sign in with UAE PASS',
  linkChoiceTitle: 'Do you have an online account with Tabbio?',
  note: "This is a one time process, your future login's through UAE PASS will be seamless.",
  linkExistingHelp:
    'Your UAE PASS account will be linked to your existing Tabbio profile',
  linkCreateHelp:
    'Your Tabbio profile will be established based on the UAE PASS account',
  existingAccountRequired:
    'An existing Tabbio account already exists for this UAE PASS profile. Please choose Yes to link it.',
  linkingTitle: 'UAE PASS – Tabbio Profile Linking',
  linkingSubtitle: '(Login with your Tabbio credentials)',
  linkExistingCtaWeb: 'Link account with UAE PASS',
  linkExistingCtaNative: 'Link account with UAE PASS',
  matchingEmailRequired:
    'Please enter the same email address provided by UAE PASS to continue linking.',
  checkEmailTitle: 'Check your email',
  checkEmailBody: 'We sent a 6-digit code to',
  verifyLinkCta: 'Verify & Link',
  registrationTitle: 'Complete Registration',
  registrationBody:
    'Your profile is pre-filled with verified UAE PASS data. Please complete the remaining fields below.',
  createAccountCta: 'Create Account',
  welcomeTitle: 'Welcome to Tabbio',
  welcomeBody: 'Your profile has been created with verified UAE PASS data',
  welcomeCta: 'Continue to set up your profile',
  verifiedSectionTitle: 'VERIFIED UAE PASS DATA',
  readOnlyNote: 'UAE PASS data is read-only and cannot be edited',
  unlinkTitle: 'Unlink UAE PASS?',
  unlinkBody:
    'Your verified UAE PASS connection will be removed from this Tabbio account. You can link it again later.',
} as const;

export const UAE_PASS_ERROR_COPY = {
  authFailed: 'Something went wrong during the login, please try again later!',
  cancelled: 'User cancelled the login',
  sop1:
    'You are not eligible to access this service. Your account is either not upgraded or you have a visitor account. Please contact TABBIO TECHNOLOGY to access the services.',
  sop2:
    'Your UAE PASS signature is not qualified. Please upgrade your account following instructions in the UAE PASS app to obtain a Qualified Signature.',
  ineligible:
    'You are not eligible to access this service. Your account is either not upgraded or you have a visitor account. Please contact TABBIO TECHNOLOGY to access the services.',
  stateFailed: 'Something went wrong during the login, please try again later!',
  sessionExpired: 'User cancelled the login',
  alreadyLinked:
    'This UAE Pass account is already linked to another Tabbio account',
  wrongAccount:
    'Please sign in with the matched Tabbio account to link UAE PASS.',
  unlinked: 'Your UAE PASS account has been disconnected.',
  alreadyUnlinked: 'UAE PASS is already disconnected on this account.',
} as const;

export type UaePassErrorType =
  | 'auth_failed'
  | 'cancelled'
  | 'ineligible'
  | 'session_expired'
  | 'sop1'
  | 'sop2'
  | 'state_failed';

const UAE_PASS_CANCEL_ERROR_CODES = new Set([
  'invalid_request',
  'login_required',
  'access_denied',
  'cancelledonapp',
]);

export function isUaePassLoginCancellation(value?: string | null) {
  const normalized = value?.trim();
  if (!normalized) {
    return false;
  }

  const code = normalized.toLowerCase();
  return (
    UAE_PASS_CANCEL_ERROR_CODES.has(code) ||
    normalized === UAE_PASS_ERROR_COPY.cancelled ||
    normalized === 'User cancelled the login' ||
    normalized === 'User cancelled the login.' ||
    normalized === 'قام المستخدم بالغاء تسجيل الدخول' ||
    normalized === 'قام المستخدم بإلغاء تسجيل الدخول'
  );
}

export function resolveUaePassErrorType(
  messageOrCode?: string,
): UaePassErrorType {
  if (!messageOrCode) {
    return 'auth_failed' as const;
  }

  const normalized = messageOrCode.trim();
  const upper = normalized.toUpperCase();

  if (isUaePassLoginCancellation(normalized)) {
    return 'cancelled' as const;
  }

  if (
    upper === 'UAE_PASS_SOP1_RESTRICTED' ||
    normalized === UAE_PASS_ERROR_COPY.sop1 ||
    normalized === UAE_PASS_ERROR_COPY.ineligible
  ) {
    return 'sop1' as const;
  }

  if (
    upper === 'UAE_PASS_SOP2_RESTRICTED' ||
    normalized === UAE_PASS_ERROR_COPY.sop2
  ) {
    return 'sop2' as const;
  }

  if (upper === 'UAE_PASS_INELIGIBLE') {
    return 'ineligible' as const;
  }

  if (upper === 'UAE_PASS_STATE_INVALID') {
    return 'state_failed' as const;
  }

  if (
    upper === 'UAE_PASS_PENDING_INVALID' ||
    normalized === 'Your UAE PASS session expired. Please sign in with UAE PASS again.' ||
    normalized === 'انتهت صلاحية جلسة الهوية الرقمية. يرجى تسجيل الدخول باستخدام الهوية الرقمية مرة أخرى.'
  ) {
    return 'cancelled' as const;
  }

  return 'auth_failed' as const;
}
